import type { ScrollToOptions } from '@tanstack/react-virtual';
import clsx from 'clsx';

import {
  type StylingProps,
  type WithChildren,
} from '@dynatrace/strato-components/core';

import { dataTableV2RowCSS } from './DataTableV2Row.sty.js';
import {
  dataTableV2FirstBodyRowCSS,
  dataTableV2LastBodyRowCSS,
} from './DataTableV2Rows.sty.js';
import { useDataTableV2 } from '../contexts/DataTableV2Context.js';
import { rowDetailsExpandedRowCellCSS } from '../features/RowDetails/RowDetails.sty.js';
import { useRowInteractivityProps } from '../features/RowInteractivity/RowInteractivity.js';
import type { Row } from '../hooks/useTable/types.js';

interface DataTableV2RowProps<TData> extends WithChildren, StylingProps {
  /**
   * DataTableV2 row object.
   */
  row: Row<TData>;
  /**
   * Total number of rows currently rendered in the table.
   */
  rowCount: number;
  /**
   * The row's index based on the virtualization.
   */
  virtualRowIndex: number;
  /**
   * If the row is currently being reordered via drag and drop.
   */
  isDraggedRow: boolean;
  /**
   * Parity of the row to keep track of the zebra stripe patterns.
   */
  rowParity?: 'even' | 'odd';
  /**
   * Measure function for row sizing that is called when the row is rendered to the dom.
   */
  rowMeasure: (node: Element | null) => void;
  /**
   * rowVirtualizer function for scrolling to the row with the specified index.
   */
  scrollToIndex: (index: number, options?: ScrollToOptions) => void;
}

/**
 * Wrapper around all cells in a row.
 * @internal
 */
export function DataTableV2Row<TData>(
  props: Readonly<DataTableV2RowProps<TData>>,
) {
  const {
    row,
    rowCount,
    virtualRowIndex,
    isDraggedRow,
    rowParity,
    rowMeasure,
    scrollToIndex,
    children,
  } = props;

  const table = useDataTableV2();

  const { rowInteractivityClassName, rowInteractivityProps } =
    useRowInteractivityProps<TData>(row, scrollToIndex);

  return (
    <div
      role="row"
      ref={rowMeasure}
      data-rowid={row.id}
      data-row={virtualRowIndex}
      className={clsx(
        dataTableV2RowCSS({
          rowSeparation: table.getVisualVariantOptions().rowSeparation,
          rowParity,
        }),
        {
          [dataTableV2FirstBodyRowCSS]: virtualRowIndex === 0,
          [dataTableV2LastBodyRowCSS]: virtualRowIndex === rowCount - 1,
          [`${rowDetailsExpandedRowCellCSS}`]:
            row.getIsDetailsOpen() && !isDraggedRow,
        },
        rowInteractivityClassName,
      )}
      style={{
        ...row.getGridRowPosition(virtualRowIndex),
      }}
      {...rowInteractivityProps}
    >
      {children}
    </div>
  );
}
